import { Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useListUsers } from '@/api/generated/users/users';
import { FormModal, ModalFooter, TextField } from '@/components/common';

interface ActivityWorkerPickerModalProps {
  opened: boolean;
  onClose: () => void;
  existingUserIds: string[];
  /** Odabrani radnici idu u nacrt (NOVO) s 0 sati — spremaju se tek gumbom "Spremi sate". */
  onAdd: (userIds: string[]) => void;
}

export const ActivityWorkerPickerModal = ({
  opened,
  onClose,
  existingUserIds,
  onAdd
}: ActivityWorkerPickerModalProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: usersData } = useListUsers();
  const usedUserIds = new Set(existingUserIds);

  const query = search.trim().toLowerCase();
  const rows = (usersData ?? [])
    .filter((user) => user.id && !usedUserIds.has(user.id))
    .map((user) => ({
      id: user.id as string,
      name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
    }))
    .filter((row) => !query || row.name.toLowerCase().includes(query));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setSelected(new Set());
  };

  const handleAdd = () => {
    onAdd(Array.from(selected));
    reset();
    onClose();
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={560}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('activityWorkerPicker.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <TextField
          label={t('activityWorkerPicker.searchLabel')}
          placeholder={t('activityWorkerPicker.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <Stack gap={0} style={{ maxHeight: 320, overflowY: 'auto' }}>
          <Group
            gap={8}
            wrap="nowrap"
            py={4}
            style={{ borderBottom: '1px solid var(--color-border-soft)' }}
          >
            <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
              {t('activityWorkerPicker.colName')}
            </Text>
          </Group>

          {rows.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('activityWorkerPicker.emptyState')}
            </Text>
          )}

          {rows.map((row, index) => (
            <Group
              key={row.id}
              gap={8}
              wrap="nowrap"
              py={10}
              role="button"
              tabIndex={0}
              onClick={() => toggle(row.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggle(row.id);
              }}
              style={{
                cursor: 'pointer',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Checkbox
                checked={selected.has(row.id)}
                onChange={() => toggle(row.id)}
                onClick={(event) => event.stopPropagation()}
              />
              <Text fz={13} fw={600} style={{ flex: 1, color: 'var(--color-text-primary)' }}>
                {row.name}
              </Text>
            </Group>
          ))}
        </Stack>

        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          {t('itemsDraft.pickerNoteHours')}
        </Text>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button disabled={selected.size === 0} onClick={handleAdd}>
            {t('activityWorkerPicker.addSelected', { count: selected.size })}
          </Button>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
